import React, { useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';

gsap.registerPlugin(ScrollTrigger, useGSAP);

/**
 * Titular que se "enciende" palabra por palabra mientras se scrollea.
 *
 * Uso:
 *   <ScrollWords as="h2" parts={[
 *     { text: 'Más que una consultora,' },
 *     { text: 'somos tu equipo extendido.', accent: true },   // serif itálica
 *   ]} />
 *
 * Con un texto de "Páginas" en Sanity: parts={partes(textos.titular)} (src/cms/textos.js).
 *
 * El texto completo queda en el DOM (SEO y lectores de pantalla lo leen
 * normal); solo se anima la opacidad de cada palabra con scrub.
 */
export default function ScrollWords({ parts, as: Tag = 'p', className = '', dim = 0.14, start = 'top 85%', end = 'bottom 45%' }) {
    const ref = useRef(null);
    // Si el texto cambia (llega de Sanity o se edita en la vista previa), las palabras son otras
    const texto = parts.map((p) => p.text).join(' ');

    useGSAP(() => {
        const words = ref.current.querySelectorAll('[data-word]');
        gsap.fromTo(words,
            { opacity: dim },
            {
                opacity: 1,
                ease: 'none',
                stagger: 0.08,
                scrollTrigger: { trigger: ref.current, start, end, scrub: true },
            });
    }, { scope: ref, dependencies: [dim, start, end, texto], revertOnUpdate: true });

    // data-sanity-edit-target: en la vista previa del panel, el recuadro para
    // editar cubre el titular entero y no solo la palabra que trae la marca
    return (
        <Tag ref={ref} className={className} data-sanity-edit-target="">
            {parts.map((part, i) => (
                <span key={i} className={part.accent ? 'font-serif italic font-normal' : undefined}>
                    {part.text.split(' ').map((word, j) => (
                        <React.Fragment key={j}>
                            <span data-word className="inline-block">{word}</span>{' '}
                        </React.Fragment>
                    ))}
                </span>
            ))}
        </Tag>
    );
}
